import React, { useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import {
  startMigration,
  retryMigration,
} from '~/store/modules/migration/actions';
import {
  getProgressPercent,
  getStepLabel,
  V2_DASHBOARD_URL,
  canAccessMigration,
} from '~/utils/migration';
import BackButtonIcon from '~/assets/icons/backButton.svg';

import {
  Container,
  Body,
  InfoCard,
  FeatureList,
  StartButton,
  RetryButton,
  ProgressSection,
  ProgressBar,
  StepInfo,
  Spinner,
  SuccessCard,
  ErrorCard,
} from './styles';

function Migration() {
  const dispatch = useDispatch();
  const { phase, migrationData } = useSelector(state => state.migration);
  const userProfile = useSelector(state => state.user.profile);
  const [isStarting, setIsStarting] = useState(false);

  if (!canAccessMigration(userProfile)) {
    return <Redirect to="/dashboard" />;
  }

  const progress = migrationData
    ? getProgressPercent(migrationData.completedSteps)
    : 0;

  function handleStartMigration() {
    setIsStarting(true);
    dispatch(startMigration());
    setTimeout(() => setIsStarting(false), 2000);
  }

  function handleRetry() {
    setIsStarting(true);
    dispatch(retryMigration());
    setTimeout(() => setIsStarting(false), 2000);
  }

  return (
    <Container>
      <header>
        <div className="div-space">
          <Link to="/dashboard">
            <img src={BackButtonIcon} alt="Voltar" />
          </Link>
        </div>
        <div>
          <span>Migrar para v2</span>
        </div>
        <div className="div-space" />
      </header>

      <Body>
        {phase === 'idle' && (
          <>
            <InfoCard>
              <h2>Migrar sua loja para a nova Offstore</h2>
              <p>
                Leve todos os seus dados para a nova plataforma. O processo é
                automático e sua loja atual continua funcionando durante a
                migração.
              </p>
            </InfoCard>

            <FeatureList>
              <li>Todos os produtos, imagens e variantes</li>
              <li>Histórico completo de pedidos e clientes</li>
              <li>Cupons de desconto e configurações de frete</li>
              <li>Gateways de pagamento (Mercado Pago / PagBank)</li>
              <li>Domínio personalizado e pixels de conversão</li>
              <li>Seu plano atual é mantido</li>
            </FeatureList>

            <StartButton
              type="button"
              onClick={handleStartMigration}
              disabled={isStarting}
            >
              {isStarting ? 'Iniciando...' : 'Iniciar migração'}
            </StartButton>
          </>
        )}

        {phase === 'in_progress' && (
          <>
            <Spinner />

            <InfoCard>
              <h2>Migração em andamento</h2>
              <p>
                Você pode navegar livremente pela Offstore enquanto a migração
                acontece. Sua loja atual continua funcionando normalmente.
              </p>
            </InfoCard>

            <ProgressSection>
              <ProgressBar>
                <div style={{ width: `${progress}%` }} />
              </ProgressBar>
              <StepInfo>
                {migrationData?.currentStep
                  ? getStepLabel(migrationData.currentStep)
                  : 'Aguardando...'}{' '}
                {progress}%
              </StepInfo>
            </ProgressSection>
          </>
        )}

        {phase === 'completed' && (
          <SuccessCard>
            <h2>Migração concluída!</h2>
            <p>
              Sua loja foi migrada com sucesso para a nova Offstore. Acesse a
              nova versão para continuar gerenciando seus produtos.
            </p>
            <a
              href={V2_DASHBOARD_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Acessar nova versão
            </a>
          </SuccessCard>
        )}

        {phase === 'failed' && (
          <>
            <ErrorCard>
              <h2>Erro na migração</h2>
              <p>
                {migrationData?.errorMessage ||
                  'Ocorreu um erro durante a migração.'}
              </p>
              <p>
                Você pode tentar novamente. Se o problema persistir, entre em
                contato com o suporte.
              </p>
            </ErrorCard>

            <RetryButton
              type="button"
              onClick={handleRetry}
              disabled={isStarting}
            >
              {isStarting ? 'Tentando...' : 'Tentar novamente'}
            </RetryButton>
          </>
        )}
      </Body>
    </Container>
  );
}

export default Migration;
